import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * TimerContext — a small global for the currently active timer so both the
 * TopBar widget and the "Start timer" buttons scattered across the app can
 * mutate it without re-implementing polling logic.
 *
 * `elapsed` ticks locally every second while a timer is running; every 60s
 * we also re-sync with the server to catch cross-tab starts/stops.
 */
const TimerContext = createContext(null);

export function TimerProvider({ children }) {
  const [active, setActive] = useState(null);   // full timer row or null
  const [elapsed, setElapsed] = useState(0);    // seconds
  const tickRef = useRef(null);
  const syncRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/time/active");
      setActive(data?.active || null);
    } catch { /* silent */ }
  }, []);

  // Initial + polling refresh (every 60s catches cross-tab activity)
  useEffect(() => {
    refresh();
    syncRef.current = setInterval(refresh, 60_000);
    return () => clearInterval(syncRef.current);
  }, [refresh]);

  // Local per-second ticker when a timer is running
  useEffect(() => {
    clearInterval(tickRef.current);
    if (!active?.started_at) { setElapsed(0); return; }
    const startMs = new Date(active.started_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
  }, [active?.started_at, active?.id]);

  const start = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/time/start", payload || {});
      setActive(data);
      toast.success(`Timer started${data.contact_name ? ` · ${data.contact_name}` : ""}`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start timer");
      throw e;
    }
  }, []);

  const stop = useCallback(async () => {
    try {
      const { data } = await api.post("/time/stop");
      setActive(null);
      const h = Math.floor(data.seconds / 3600), m = Math.floor((data.seconds % 3600) / 60), s = data.seconds % 60;
      const amount = data.billable ? ` · $${((data.seconds / 3600) * data.hourly_rate).toFixed(2)}` : "";
      toast.success(`Logged ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}${amount}`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop timer");
    }
  }, []);

  const discard = useCallback(async () => {
    try {
      await api.post("/time/discard");
      setActive(null);
      toast("Timer discarded", { description: "Nothing was recorded." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not discard timer");
    }
  }, []);

  return (
    <TimerContext.Provider value={{ active, elapsed, refresh, start, stop, discard }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error("useTimer must be used inside <TimerProvider>");
  return ctx;
}

export function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
